/**
 * Modal voor het aanmaken van een nieuwe branch vanuit de branch-keuzelijst
 * in de Explorer (de optie `explorer.newBranchOption`). Daily-driver-flow:
 *   1. Naam invoeren
 *   2. Bronbranch kiezen (default = main van huidige workpackage)
 *   3. Optioneel: starten vanaf een snapshot van de bron-main
 *   4. OK → branch wordt aangemaakt; caller switcht meteen naar 'm
 *
 * Gebruikt `api.createBranch` (workspace-niveau). De workpackage van de
 * nieuwe branch wordt door de worker afgeleid uit `from_branch_id`
 * (of `from_snapshot_id` → branch → workpackage).
 */
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type BranchSummary, type SnapshotMetadata } from "../api/client";

interface Props {
  workspaceId: string;
  workpackageId: string | null;
  onClose: () => void;
  onCreated: (newBranchId: string) => void;
}

export function NewBranchModal({ workspaceId, workpackageId, onClose, onCreated }: Props) {
  const { t } = useTranslation();

  const [wpBranches, setWpBranches] = useState<BranchSummary[]>([]);
  const mainBranchId = useMemo(
    () => wpBranches.find((b) => b.is_main)?.id ?? wpBranches[0]?.id ?? "",
    [wpBranches],
  );

  const [name, setName] = useState("");
  const [fromBranch, setFromBranch] = useState<string>("");
  const [fromSnapshot, setFromSnapshot] = useState<string>("");
  const [snapshots, setSnapshots] = useState<SnapshotMetadata[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Haal WP-scoped branches op zodra de modal opent. Active-only is hier
  // niet logisch want je kunt ook van een merged-branch nog clonen.
  useEffect(() => {
    if (!workpackageId) {
      setWpBranches([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.listWorkpackageBranches(workspaceId, workpackageId);
        // Alleen werkbare ouders: onder een samengevoegde of opgegeven branch
        // hoort niets nieuws te ontstaan.
        if (!cancelled) setWpBranches(res.branches.filter((b) => b.status === "active"));
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, workpackageId]);

  // Init from-branch zodra branches binnen zijn.
  useEffect(() => {
    if (!fromBranch && mainBranchId) {
      setFromBranch(mainBranchId);
    }
  }, [fromBranch, mainBranchId]);

  // Laad snapshots van de gekozen bron-branch (alleen die branch — niet alle).
  useEffect(() => {
    if (!fromBranch) {
      setSnapshots([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.listSnapshots(workspaceId, fromBranch, false);
        if (!cancelled) setSnapshots(res.items);
      } catch {
        if (!cancelled) setSnapshots([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, fromBranch]);

  // Escape sluit de modal (mits niet midden in een POST).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("branchPicker.modal.errorEmptyName"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: Parameters<typeof api.createBranch>[1] = { name: trimmed };
      if (fromSnapshot) {
        body.from_snapshot_id = fromSnapshot;
      } else if (fromBranch) {
        body.from_branch_id = fromBranch;
      }
      const created = await api.createBranch(workspaceId, body);
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationele overlay-laag; sluiten via Escape + knoppen, klik-buiten is muis-gemak
    <div
      className="ledger fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="presentation"
    >
      <form
        onSubmit={submit}
        className="flex w-full max-w-md flex-col gap-4 rounded-lg border border-(--ld-control) bg-(--ld-surface) p-5 shadow-xl"
        role="dialog"
        aria-modal="true"
      >
        <h2 className="font-semibold text-base text-(--ld-text)">
          {t("branchPicker.modal.title")}
        </h2>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-(--ld-text)">{t("branchPicker.modal.name")}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("branchPicker.modal.namePlaceholder")}
            className="rounded border border-(--ld-muted) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
            // biome-ignore lint/a11y/noAutofocus: bewuste focus-op-openen zodat de gebruiker meteen kan typen
            autoFocus
            disabled={busy}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-(--ld-text)">{t("branchPicker.modal.fromBranch")}</span>
          <select
            value={fromBranch}
            onChange={(e) => {
              setFromBranch(e.target.value);
              setFromSnapshot("");
            }}
            className="rounded border border-(--ld-muted) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none disabled:opacity-50"
            disabled={busy || wpBranches.length === 0}
          >
            {wpBranches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
                {b.is_main ? ` (${t("branchPicker.modal.main")})` : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-(--ld-text)">{t("branchPicker.modal.fromSnapshot")}</span>
          <select
            value={fromSnapshot}
            onChange={(e) => setFromSnapshot(e.target.value)}
            className="rounded border border-(--ld-muted) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none disabled:opacity-50"
            disabled={busy || snapshots.length === 0}
          >
            <option value="">{t("branchPicker.modal.snapshotNone")}</option>
            {snapshots.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label ?? s.id.slice(0, 8)}
                {s.created_at ? ` — ${new Date(s.created_at).toLocaleString()}` : ""}
              </option>
            ))}
          </select>
          {snapshots.length === 0 && fromBranch && (
            <span className="text-(--ld-muted) text-xs italic">
              {t("branchPicker.modal.noSnapshots")}
            </span>
          )}
        </label>

        {error && (
          <p className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1.5 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:opacity-50"
          >
            {t("branchPicker.modal.cancel")}
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
          >
            {busy ? t("branchPicker.modal.creating") : t("branchPicker.modal.create")}
          </button>
        </div>
      </form>
    </div>
  );
}
